import { useNavigate, useLocation } from "react-router-dom";
import { useState, createContext, useContext, useRef } from 'react';
import { Tooltip, Drawer, useMediaQuery, useTheme, Collapse, Popover } from '@mui/material';
import { useTranslation } from 'react-i18next';
import Iconify from "src/components/Iconify";
import { verifyRules, validaMenu, verifyFeatures, hasFeatureAccess, FEATURE_KEYS } from "src/utils/functions";
import { META_ADS_DISPONIVEL } from 'src/utils/metaAppReview';
import { useSelector, useDispatch } from 'src/redux/store';
import { setChatInfo } from 'src/redux/slices/chat';
import { setMessages } from 'src/redux/slices/staff';
import useAuth from 'src/hooks/useAuth';
import Logo from 'src/components/Logo';
import NotificationsPopover from './NotificationsPopover';
import { DropdownAccountNav } from 'src/@/components/use/dropdownMenu/DropdownAccountNav';
import { HelpComponent } from 'src/sections/@global/help';
import { InternetConnectionAlertHeader } from 'src/sections/@global/connection/InternetConnectionAlertHeader';
import { ZuiTooltip } from 'src/@/components/use/tooltip';
import { getSpent } from 'src/helpers/cliente.helper';
import { useEffect } from 'react';
import { sidebarDesign as ds, creditsTone, formatCredits, formatCreditsCompact } from './MainSidebar.design';
import { NavScrollArea } from './NavScrollArea';
import { useSomNotificacao, usePermissaoNotificacao } from 'src/hooks/useNotificacoes';

const SidebarContext = createContext({ isExpanded: false });
const FlyoutCloseContext = createContext(null);

const SidebarMenuButton = ({ title, onClick, slug, icon, badge = 0, badgeTone = 'neutral', isSubItem = false, tag }) => {
    const { isExpanded } = useContext(SidebarContext);
    const fecharFlyout = useContext(FlyoutCloseContext);
    const location = useLocation();
    const path = location.pathname.substring(1);
    const active = path.includes(slug);

    const handleClick = () => {
        onClick?.();
        fecharFlyout?.();
    };

    // Colapsado: apenas o icone, com tooltip lateral
    if (!isExpanded) {
        return (
            <Tooltip title={title} placement="right" arrow>
                <button
                    onClick={handleClick}
                    className={`${ds.itemCollapsed.base} ${active ? ds.itemCollapsed.active : ds.itemCollapsed.idle}`}
                >
                    {icon && <Iconify icon={icon} width={15} height={15} />}
                    {badge > 0 && <span className={ds.badge.dot} />}
                </button>
            </Tooltip>
        );
    }

    // Subitem: texto puro dentro do trilho vertical
    if (isSubItem) {
        return (
            <button
                onClick={handleClick}
                className={`${ds.subItem.base} ${active ? ds.subItem.active : ds.subItem.idle}`}
            >
                <span className="flex-1 truncate text-left">{title}</span>
                {tag && <span className={ds.badge.newTag}>{tag}</span>}
                {badge > 0 && (
                    <span className={`${ds.badge.base} ${ds.badge[badgeTone] || ds.badge.neutral}`}>{badge}</span>
                )}
            </button>
        );
    }

    return (
        <button
            onClick={handleClick}
            className={`${ds.item.base} ${active ? ds.item.active : ds.item.idle}`}
        >
            {active && <span className={ds.item.accent} />}
            {icon && (
                <Iconify
                    icon={icon}
                    width={14}
                    height={14}
                    className={`${ds.item.icon} ${active ? ds.item.iconActive : ds.item.iconIdle}`}
                />
            )}
            <span className={ds.item.label}>{title}</span>
            {badge > 0 && (
                <span className={`${ds.badge.base} ${ds.badge[badgeTone] || ds.badge.neutral}`}>{badge}</span>
            )}
        </button>
    );
};

const SidebarExpandableMenu = ({ title, icon, children, defaultOpen = false, activeSlug }) => {
    const { isExpanded } = useContext(SidebarContext);
    const [isOpen, setIsOpen] = useState(defaultOpen);
    const [flyoutAnchor, setFlyoutAnchor] = useState(null);
    const blocoRef = useRef(null);
    const location = useLocation();
    const path = location.pathname.substring(1);
    const flyoutActive = activeSlug ? path.includes(activeSlug) : false;

    const fecharFlyout = () => setFlyoutAnchor(null);

    // Rola so depois que o Collapse terminou de animar — durante a transicao a
    // altura ainda esta crescendo e o scroll pararia curto. `block: 'nearest'`
    // move o minimo necessario, entao nada acontece se o submenu ja estava visivel.
    const acompanharScroll = () => {
        blocoRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    };

    // Sidebar recolhida: icone + submenu lateral (Popover).
    if (!isExpanded) {
        return (
            <FlyoutCloseContext.Provider value={fecharFlyout}>
                <Tooltip title={title} placement="right" arrow>
                    <button
                        type="button"
                        onClick={(e) => setFlyoutAnchor(e.currentTarget)}
                        className={`${ds.itemCollapsed.base} ${flyoutActive ? ds.itemCollapsed.active : ds.itemCollapsed.idle}`}
                    >
                        {icon && <Iconify icon={icon} width={15} height={15} />}
                    </button>
                </Tooltip>
                <Popover
                    open={Boolean(flyoutAnchor)}
                    anchorEl={flyoutAnchor}
                    onClose={fecharFlyout}
                    anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    slotProps={{
                        paper: {
                            className: ds.flyout.paper,
                            elevation: 8,
                        },
                    }}
                >
                    <div className={ds.flyout.header}>{title}</div>
                    <div className={ds.flyout.list}>
                        <SidebarContext.Provider value={{ isExpanded: true }}>
                            {children}
                        </SidebarContext.Provider>
                    </div>
                </Popover>
            </FlyoutCloseContext.Provider>
        );
    }

    return (
        <div ref={blocoRef}>
            <button
                onClick={() => setIsOpen(!isOpen)}
                className={`${ds.item.base} ${ds.item.idle}`}
            >
                {icon && (
                    <Iconify
                        icon={icon}
                        width={14}
                        height={14}
                        className={`${ds.item.icon} ${ds.item.iconIdle}`}
                    />
                )}
                <span className={ds.item.label}>{title}</span>
                <Iconify
                    icon="lucide:chevron-down"
                    width={12}
                    height={12}
                    className={`${ds.item.chevron} ${isOpen ? 'rotate-180' : ''}`}
                />
            </button>
            <Collapse in={isOpen} onEntered={acompanharScroll}>
                <div className={ds.subItem.rail}>
                    {children}
                </div>
            </Collapse>
        </div>
    );
};

const SidebarGroup = ({ children, label }) => {
    const { isExpanded } = useContext(SidebarContext);

    return (
        <div className="mb-1 last:mb-0">
            {label && isExpanded && (
                <div className={ds.nav.groupLabel}>{label}</div>
            )}
            <div className={ds.nav.list}>
                {children}
            </div>
        </div>
    );
};

export default function MainSidebar() {
    const { user, cliente } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const theme = useTheme();
    const { t } = useTranslation('navigation');
    const { t: tNotif } = useTranslation('notifications');
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { roomsWithNewMessages } = useSelector((state) => state.staff);
    const { flag } = useSelector((state) => state.global);

    // Anúncios Meta exige as duas coisas: o plugin contratado pelo cliente e a
    // permissão concedida ao app na Meta (ver metaAppReview) — sem o escopo a
    // tela não teria dado nenhum, então o item nem entra no menu.
    const podeVerAnunciosMeta = META_ADS_DISPONIVEL
        && hasFeatureAccess(cliente, user, FEATURE_KEYS.MARKETING_ADS);

    // Por padrao, comeca colapsado (nao expandido)
    const [isExpanded, setIsExpanded] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [saldo, setSaldo] = useState(0);
    const [credit, setCredit] = useState(0);
    const [isLoadingSaldo, setIsLoadingSaldo] = useState(false);

    // Antes lia o localStorage direto e chamava window.location.reload() para o
    // resto do app enxergar a mudanca — o reload derrubava o socket e o estado de
    // digitacao. O hook mantem os toggles das outras telas em sincronia.
    const { ligado: somLigado, alternar: alternarSom } = useSomNotificacao();
    const { estado: permissaoNotificacao, pedir: pedirPermissaoNotificacao } = usePermissaoNotificacao();
    const isAdmin = user?.level === "root";
    const isNegative = saldo < 0;
    const tone = creditsTone(isNegative);
    const settingsActive = location.pathname.includes('dashboard/configuracoes');

    async function handleGetSaldo() {
        try {
            setIsLoadingSaldo(true);
            const hoje = new Date();
            const startDate = hoje.toISOString().split('T')[0];
            const endDate = hoje.toISOString().split('T')[0];
            const querys = `?inicio=${startDate}&final=${endDate}&periodo=mes_atual`;
            const response = await getSpent(user.clienteId, querys);
            const limiteRestante = response?.data?.credits?.total?.limit - response?.data?.credits?.total?.use || 0;
            const saldoDisponivel = limiteRestante * 0.33;
            setSaldo(saldoDisponivel);
            setCredit(limiteRestante);
        } catch (error) {
            console.error('Erro ao buscar saldo:', error);
        } finally {
            setIsLoadingSaldo(false);
        }
    }

    useEffect(() => {
        handleGetSaldo();
    }, []);

    const handleNavigate = (path) => {
        navigate(path);
        if (isMobile) setMobileOpen(false);
    };

    const creditsTooltipContent = (
        <div className="space-y-1 p-1 mt-2">
            <div className="font-semibold text-xs text-gray-900">{t('navbar.credits')}</div>
            <div className="text-[10px] text-gray-500 space-y-0.5">
                <div>{t('navbar.balance')}: <span className="font-medium text-gray-700">{formatCredits(credit)}</span> {t('navbar.credits').toLowerCase()}</div>
                <div>{t('navbar.status')}: <span className={isNegative ? 'text-red-500' : 'text-green-600'}>{isNegative ? t('common:status.negative') : t('common:status.positive')}</span></div>
            </div>
        </div>
    );

    // Linha de creditos (sidebar expandida e drawer mobile)
    const creditsRow = (
        <ZuiTooltip content={creditsTooltipContent}>
            <div
                role="button"
                tabIndex={0}
                onClick={() => handleNavigate('/dashboard/conta/consumo')}
                onKeyDown={(e) => { if (e.key === 'Enter') handleNavigate('/dashboard/conta/consumo'); }}
                className={`${ds.footer.credits} cursor-pointer`}
            >
                <span className={`${ds.footer.creditsAccent} ${tone.accent}`} />
                <div className="flex flex-1 min-w-0 items-baseline gap-[7px]">
                    <span className={`${ds.footer.creditsValue} ${tone.value}`}>{formatCredits(credit)}</span>
                    <span className={ds.footer.creditsLabel}>{t('navbar.credits').toLowerCase()}</span>
                </div>
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        handleGetSaldo();
                    }}
                    className={ds.footer.creditsRefresh}
                    disabled={isLoadingSaldo}
                    title={t('navbar.credits')}
                >
                    <Iconify
                        icon="lucide:refresh-cw"
                        width={13}
                        className={isLoadingSaldo ? 'animate-spin' : ''}
                    />
                </button>
            </div>
        </ZuiTooltip>
    );

    // Linha de acoes: configuracoes + som + notificacoes + ajuda
    const footerActions = (
        <div className={ds.footer.actions}>
            {verifyRules(user.rules, "configuracoes") && (
                <button
                    onClick={() => handleNavigate('/dashboard/configuracoes/canais')}
                    className={`${ds.footer.settings} ${settingsActive ? ds.footer.settingsActive : ds.footer.settingsIdle}`}
                >
                    <Iconify icon="lucide:settings" width={13} height={13} className="flex-shrink-0" />
                    <span className="truncate">{t('sidebar.settings')}</span>
                </button>
            )}

            <Tooltip title={somLigado ? t('navbar.soundOn') : t('navbar.soundOff')} placement="top">
                <button
                    onClick={() => alternarSom()}
                    className={`${ds.footer.iconAction} ml-auto`}
                >
                    <Iconify
                        icon={somLigado ? "solar:volume-loud-bold-duotone" : "solar:volume-cross-bold-duotone"}
                        width={15}
                    />
                </button>
            </Tooltip>

            {/* Sem este indicador, notificacao bloqueada no navegador ficava
                invisivel: o usuario achava que o painel estava mudo por bug. */}
            {permissaoNotificacao !== 'granted' && permissaoNotificacao !== 'unsupported' && (
                <Tooltip title={tNotif('permissao.desativadas')} placement="top">
                    <button onClick={() => pedirPermissaoNotificacao()} className={ds.footer.iconAction}>
                        <Iconify icon="eva:bell-off-outline" width={15} className="text-amber-500" />
                    </button>
                </Tooltip>
            )}

            <span className={ds.footer.iconSlot}>
                <NotificationsPopover />
            </span>
            <span className={ds.footer.iconSlot}>
                <HelpComponent />
            </span>
        </div>
    );

    const footerExpanded = (
        <div className={ds.footer.expanded}>
            {creditsRow}
            {footerActions}
            <InternetConnectionAlertHeader />
            <div className={ds.footer.account}>
                <DropdownAccountNav variant="full" />
            </div>
        </div>
    );

    const sidebarContent = (
        <aside className={`${ds.shell.base} ${isMobile ? ds.shell.mobile : (isExpanded ? ds.shell.expanded : ds.shell.collapsed)}`}>
            {/* Header com Logo */}
            {isExpanded ? (
                <div className={ds.header.expanded}>
                    <Logo />
                    {isMobile ? (
                        <button
                            onClick={() => setMobileOpen(false)}
                            className={ds.header.toggle}
                        >
                            <Iconify icon="heroicons:x-mark" width={14} />
                        </button>
                    ) : (
                        <button
                            onClick={() => setIsExpanded(false)}
                            className={ds.header.toggle}
                            title={t('navbar.collapseMenu')}
                        >
                            <Iconify icon="solar:alt-arrow-left-linear" width={13} />
                        </button>
                    )}
                </div>
            ) : (
                <div className={ds.header.collapsed}>
                    <Tooltip title={t('navbar.expandMenu')} placement="right">
                        <button
                            onClick={() => setIsExpanded(true)}
                            className={ds.header.logoTile}
                        >
                            <Iconify icon="tabler:layout-sidebar-left-expand-filled" width={16} />
                        </button>
                    </Tooltip>
                </div>
            )}

            {/* Flag de alerta */}
            {flag?.status && isExpanded && (
                <div className={`${ds.flag} bg-${flag?.flag}-600`}>
                    {flag?.msg}
                </div>
            )}

            {/* Menu de navegacao */}
            <NavScrollArea className={`${isExpanded ? ds.nav.expanded : ds.nav.collapsed} ${ds.scrollbar}`}>
                <nav className={isExpanded ? '' : 'flex flex-col items-center gap-1'}>
                    <SidebarGroup label={t('sidebar.groups.main')}>
                        {verifyRules(user.rules, "chats") && (
                            <SidebarMenuButton
                                title={t('sidebar.conversations')}
                                slug="chats/chat"
                                icon="lucide:message-circle"
                                onClick={() => {
                                    handleNavigate('/chats');
                                    dispatch(setChatInfo(null));
                                    dispatch(setMessages([]));
                                }}
                            />
                        )}

                        {hasFeatureAccess(cliente, user, FEATURE_KEYS.CHAT_ARCHIVE) && (
                            <SidebarMenuButton
                                title="Arquivadas"
                                slug="chats/arquivadas"
                                icon="lucide:archive"
                                onClick={() => handleNavigate('/chats/arquivadas')}
                            />
                        )}

                        {verifyRules(user.rules, "chat_interno") && (
                            <SidebarMenuButton
                                title={t('sidebar.internalChat')}
                                slug="chats/interno"
                                icon="lucide:users-round"
                                badge={roomsWithNewMessages.length}
                                onClick={() => handleNavigate('/chats/interno')}
                            />
                        )}

                        {verifyRules(user.rules, "chamados") && (
                            <SidebarMenuButton
                                title={t('sidebar.funnel')}
                                slug="dashboard/negocios"
                                icon="lucide:filter"
                                onClick={() => handleNavigate('/dashboard/negocios')}
                            />
                        )}

                        <SidebarMenuButton
                            title={t('sidebar.contacts')}
                            slug="dashboard/contatos"
                            icon="lucide:contact"
                            onClick={() => handleNavigate('/dashboard/contatos')}
                        />

                        {verifyRules(user.rules, "chamados") && (
                            <SidebarMenuButton
                                title={t('sidebar.tickets')}
                                slug="dashboard/chamados"
                                icon="lucide:ticket"
                                onClick={() => handleNavigate('/dashboard/chamados-chat')}
                            />
                        )}

                        {verifyFeatures(cliente?.features, "calendarios") && (
                            <SidebarMenuButton
                                title={t('sidebar.calendar')}
                                slug="dashboard/calendario"
                                icon="lucide:calendar"
                                onClick={() => handleNavigate('/dashboard/calendario')}
                            />
                        )}

                        {verifyRules(user.rules, "vendas") && (
                            <SidebarMenuButton
                                title={t('sidebar.sales')}
                                slug="dashboard/vendas"
                                icon="lucide:shopping-bag"
                                onClick={() => handleNavigate('/dashboard/vendas')}
                            />
                        )}

                        {verifyRules(user.rules, "automacoes") && isExpanded && (
                            <SidebarMenuButton
                                title={t('sidebar.automations')}
                                slug="dashboard/automacoes"
                                icon="lucide:workflow"
                                onClick={() => handleNavigate('/dashboard/automacoes')}
                            />
                        )}

                        {isExpanded && (
                            <SidebarMenuButton
                                title={t('sidebar.forms')}
                                slug="dashboard/formularios"
                                icon="lucide:file-text"
                                onClick={() => handleNavigate('/dashboard/formularios')}
                            />
                        )}

                        {isExpanded && (
                            <SidebarMenuButton
                                title={t('sidebar.tasks')}
                                slug="dashboard/tarefas"
                                icon="lucide:check-square"
                                onClick={() => handleNavigate('/dashboard/tarefas')}
                            />
                        )}

                        {isExpanded && hasFeatureAccess(cliente, user, FEATURE_KEYS.PROPOSALS) && (
                            <SidebarMenuButton
                                title={t('sidebar.propostas')}
                                slug="dashboard/propostas"
                                icon="lucide:file-text"
                                onClick={() => handleNavigate('/dashboard/propostas')}
                            />
                        )}
                    </SidebarGroup>

                    {/* Marketing */}
                    {(validaMenu(user ? user.rules : [''], ['agendamentos', 'campanhas'])
                        || hasFeatureAccess(cliente, user, FEATURE_KEYS.MARKETING_POSTS)
                        || podeVerAnunciosMeta) && (
                        <>
                            <div className={isExpanded ? ds.nav.divider : ds.nav.dividerCollapsed} />
                            <SidebarExpandableMenu
                                title={t('sidebar.marketing')}
                                icon="lucide:megaphone"
                                activeSlug="dashboard/marketing"
                            >
                                {verifyRules(user.rules, 'agendamentos') && (
                                    <SidebarMenuButton
                                        title={t('sidebar.schedules')}
                                        slug="dashboard/marketing/agendamentos"
                                        isSubItem
                                        onClick={() => handleNavigate('/dashboard/marketing/agendamentos')}
                                    />
                                )}
                                {verifyRules(user.rules, 'campanhas') && (
                                    <SidebarMenuButton
                                        title="Campanhas WhatsApp"
                                        slug="dashboard/marketing/campanhas"
                                        isSubItem
                                        onClick={() => handleNavigate('/dashboard/marketing/campanhas')}
                                    />
                                )}
                                {verifyRules(user.rules, 'campanhas') && (
                                    <SidebarMenuButton
                                        title={t('sidebar.tracking')}
                                        slug="dashboard/marketing/rastreamento"
                                        isSubItem
                                        onClick={() => handleNavigate('/dashboard/marketing/rastreamento')}
                                    />
                                )}
                                {verifyRules(user.rules, 'campanhas') && (
                                    <SidebarMenuButton
                                        title={t('sidebar.ctwa')}
                                        slug="dashboard/marketing/ctwa"
                                        isSubItem
                                        tag={t('sidebar.newBadge')}
                                        onClick={() => handleNavigate('/dashboard/marketing/ctwa')}
                                    />
                                )}
                                {hasFeatureAccess(cliente, user, FEATURE_KEYS.MARKETING_POSTS) && (
                                    <SidebarMenuButton
                                        title={t('sidebar.posts')}
                                        slug="dashboard/marketing/publicacoes"
                                        isSubItem
                                        onClick={() => handleNavigate('/dashboard/marketing/publicacoes')}
                                    />
                                )}
                                {podeVerAnunciosMeta && (
                                    <SidebarMenuButton
                                        title="Anúncios Meta"
                                        slug="dashboard/marketing/anuncios"
                                        isSubItem
                                        onClick={() => handleNavigate('/dashboard/marketing/anuncios')}
                                    />
                                )}
                            </SidebarExpandableMenu>
                        </>
                    )}

                    {(isAdmin || verifyRules(user.rules, 'lider_setor')) && (
                        <>
                            <div className={isExpanded ? ds.nav.divider : ds.nav.dividerCollapsed} />
                            <SidebarGroup label={t('sidebar.groups.admin')}>
                                <SidebarMenuButton
                                    title={t('sidebar.metrics')}
                                    slug="dashboard/home"
                                    icon="tabler:brand-google-analytics"
                                    onClick={() => handleNavigate('/dashboard/home')}
                                />
                                <SidebarMenuButton
                                    title={t('sidebar.supervise')}
                                    slug="dashboard/supervisao"
                                    icon="lucide:eye"
                                    onClick={() => handleNavigate('/dashboard/supervisao/lista')}
                                />

                                {(verifyRules(user.rules, 'relatorios') || verifyRules(user.rules, 'lider_setor')) && (
                                    <SidebarExpandableMenu
                                        title={t('sidebar.reports')}
                                        icon="lucide:file-bar-chart"
                                        activeSlug="dashboard/relatorios"
                                    >
                                        <SidebarMenuButton
                                            title={t('sidebar.reviews')}
                                            slug="dashboard/relatorios/chats-avaliacoes"
                                            isSubItem
                                            onClick={() => handleNavigate('/dashboard/relatorios/chats-avaliacoes')}
                                        />
                                        <SidebarMenuButton
                                            title={t('sidebar.tickets')}
                                            slug="dashboard/relatorios/chamados"
                                            isSubItem
                                            onClick={() => handleNavigate('/dashboard/relatorios/chamados')}
                                        />
                                    </SidebarExpandableMenu>
                                )}

                                {user.level === 'root' && (
                                    <SidebarExpandableMenu title={t('sidebar.ai')} icon="lucide:brain" activeSlug="dashboard/ai">
                                        <SidebarMenuButton
                                            title={t('sidebar.serviceAnalysis')}
                                            slug="dashboard/ai/analises"
                                            isSubItem
                                            onClick={() => handleNavigate('/dashboard/ai/analises')}
                                        />
                                    </SidebarExpandableMenu>
                                )}
                            </SidebarGroup>
                        </>
                    )}
                </nav>
            </NavScrollArea>

            {/* Footer com acoes */}
            {isExpanded ? footerExpanded : (
                <div className={ds.footer.collapsed}>
                    <Tooltip title={`${formatCredits(credit)} ${t('navbar.credits').toLowerCase()}`} placement="right">
                        <button
                            onClick={() => handleNavigate('/dashboard/conta/consumo')}
                            className={`${ds.footer.creditsChip} ${tone.chip}`}
                        >
                            <span className={ds.footer.creditsChipValue}>{formatCreditsCompact(credit)}</span>
                        </button>
                    </Tooltip>

                    {verifyRules(user.rules, "configuracoes") && (
                        <Tooltip title={t('sidebar.settings')} placement="right">
                            <button
                                onClick={() => handleNavigate('/dashboard/configuracoes/canais')}
                                className={`${ds.footer.iconAction} ${settingsActive ? 'bg-[#4d3568] text-[#f7f3fb]' : ''}`}
                            >
                                <Iconify icon="lucide:settings" width={15} />
                            </button>
                        </Tooltip>
                    )}

                    <Tooltip title={somLigado ? t('navbar.soundOn') : t('navbar.soundOff')} placement="right">
                        <button
                            onClick={() => alternarSom()}
                            className={ds.footer.iconAction}
                        >
                            <Iconify
                                icon={somLigado ? "solar:volume-loud-bold-duotone" : "solar:volume-cross-bold-duotone"}
                                width={15}
                            />
                        </button>
                    </Tooltip>

                    {permissaoNotificacao !== 'granted' && permissaoNotificacao !== 'unsupported' && (
                        <Tooltip title={tNotif('permissao.desativadas')} placement="right">
                            <button onClick={() => pedirPermissaoNotificacao()} className={ds.footer.iconAction}>
                                <Iconify icon="eva:bell-off-outline" width={15} className="text-amber-500" />
                            </button>
                        </Tooltip>
                    )}

                    <span className={ds.footer.iconSlot}>
                        <NotificationsPopover />
                    </span>
                    <span className={ds.footer.iconSlot}>
                        <HelpComponent />
                    </span>

                    <InternetConnectionAlertHeader />

                    <div className={ds.nav.dividerCollapsed} />

                    <DropdownAccountNav variant="icon" />
                </div>
            )}
        </aside>
    );

    // Mobile: header fixo com botao para abrir drawer
    if (isMobile) {
        return (
            <SidebarContext.Provider value={{ isExpanded: true }}>
                {/* Header mobile fixo */}
                <header className="fixed top-0 left-0 right-0 z-30 h-14 bg-white dark:bg-neutral-900 border-b border-gray-200 dark:border-neutral-800 flex items-center px-4 justify-between">
                    <div className="flex items-center gap-3">
                        <button
                            onClick={() => setMobileOpen(true)}
                            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-neutral-800 text-gray-600 dark:text-gray-300"
                        >
                            <Iconify icon="solar:hamburger-menu-linear" width={22} />
                        </button>
                        <Logo />
                    </div>

                    <div className="flex items-center gap-2">
                        <NotificationsPopover />
                        <DropdownAccountNav variant="icon" />
                    </div>
                </header>

                {/* Drawer mobile - sempre expandido */}
                <Drawer
                    anchor="left"
                    open={mobileOpen}
                    onClose={() => setMobileOpen(false)}
                    PaperProps={{
                        sx: {
                            backgroundColor: 'transparent',
                            boxShadow: 'none',
                        }
                    }}
                >
                    <SidebarContext.Provider value={{ isExpanded: true }}>
                        <aside className={`${ds.shell.base} ${ds.shell.mobile}`}>
                            {/* Header com Logo */}
                            <div className={ds.header.expanded}>
                                <Logo />
                                <button
                                    onClick={() => setMobileOpen(false)}
                                    className={ds.header.toggle}
                                >
                                    <Iconify icon="heroicons:x-mark" width={14} />
                                </button>
                            </div>

                            {/* Flag de alerta */}
                            {flag?.status && (
                                <div className={`${ds.flag} bg-${flag?.flag}-600`}>
                                    {flag?.msg}
                                </div>
                            )}

                            {/* Menu de navegacao */}
                            <div className={`${ds.nav.expanded} ${ds.scrollbar}`}>
                                <nav>
                                    <SidebarGroup label={t('sidebar.groups.main')}>
                                        {verifyRules(user.rules, "chats") && (
                                            <SidebarMenuButton
                                                title={t('sidebar.conversations')}
                                                slug="chats/chat"
                                                icon="lucide:message-circle"
                                                onClick={() => {
                                                    handleNavigate('/chats');
                                                    dispatch(setChatInfo(null));
                                                    dispatch(setMessages([]));
                                                }}
                                            />
                                        )}

                                        {hasFeatureAccess(cliente, user, FEATURE_KEYS.CHAT_ARCHIVE) && (
                                            <SidebarMenuButton
                                                title="Arquivadas"
                                                slug="chats/arquivadas"
                                                icon="lucide:archive"
                                                onClick={() => handleNavigate('/chats/arquivadas')}
                                            />
                                        )}

                                        {verifyRules(user.rules, "chat_interno") && (
                                            <SidebarMenuButton
                                                title={t('sidebar.internalChat')}
                                                slug="chats/interno"
                                                icon="lucide:users-round"
                                                badge={roomsWithNewMessages.length}
                                                onClick={() => handleNavigate('/chats/interno')}
                                            />
                                        )}

                                        {verifyRules(user.rules, "chamados") && (
                                            <SidebarMenuButton
                                                title={t('sidebar.funnel')}
                                                slug="dashboard/negocios"
                                                icon="lucide:filter"
                                                onClick={() => handleNavigate('/dashboard/negocios')}
                                            />
                                        )}

                                        <SidebarMenuButton
                                            title={t('sidebar.contacts')}
                                            slug="dashboard/contatos"
                                            icon="lucide:contact"
                                            onClick={() => handleNavigate('/dashboard/contatos')}
                                        />

                                        {verifyRules(user.rules, "chamados") && (
                                            <SidebarMenuButton
                                                title={t('sidebar.tickets')}
                                                slug="dashboard/chamados"
                                                icon="lucide:ticket"
                                                onClick={() => handleNavigate('/dashboard/chamados-chat')}
                                            />
                                        )}

                                        {verifyRules(user.rules, "calendario") && verifyFeatures(cliente?.features, "lais_calendario") && (
                                            <SidebarMenuButton
                                                title={t('sidebar.calendar')}
                                                slug="dashboard/calendario"
                                                icon="lucide:calendar"
                                                onClick={() => handleNavigate('/dashboard/calendario')}
                                            />
                                        )}

                                        {verifyRules(user.rules, "vendas") && (
                                            <SidebarMenuButton
                                                title={t('sidebar.sales')}
                                                slug="dashboard/vendas"
                                                icon="lucide:shopping-bag"
                                                onClick={() => handleNavigate('/dashboard/vendas')}
                                            />
                                        )}

                                        {verifyRules(user.rules, "automacoes") && (
                                            <SidebarMenuButton
                                                title={t('sidebar.automations')}
                                                slug="dashboard/automacoes"
                                                icon="lucide:workflow"
                                                onClick={() => handleNavigate('/dashboard/automacoes')}
                                            />
                                        )}

                                        <SidebarMenuButton
                                            title="Plugins"
                                            slug="dashboard/configuracoes/plugins"
                                            icon="lucide:puzzle"
                                            onClick={() => handleNavigate('/dashboard/configuracoes/plugins')}
                                        />
                                    </SidebarGroup>

                                    <div className={ds.nav.divider} />

                                    {/* Marketing */}
                                    {(validaMenu(user ? user.rules : [''], ['agendamentos', 'campanhas'])
                                        || hasFeatureAccess(cliente, user, FEATURE_KEYS.MARKETING_POSTS)
                                        || podeVerAnunciosMeta) && (
                                        <SidebarExpandableMenu title={t('sidebar.marketing')} icon="lucide:megaphone">
                                            {verifyRules(user.rules, 'agendamentos') && (
                                                <SidebarMenuButton
                                                    title={t('sidebar.schedules')}
                                                    slug="dashboard/marketing/agendamentos"
                                                    isSubItem
                                                    onClick={() => handleNavigate('/dashboard/marketing/agendamentos')}
                                                />
                                            )}
                                            {verifyRules(user.rules, 'campanhas') && (
                                                <SidebarMenuButton
                                                    title="Campanhas WhatsApp"
                                                    slug="dashboard/marketing/campanhas"
                                                    isSubItem
                                                    onClick={() => handleNavigate('/dashboard/marketing/campanhas')}
                                                />
                                            )}
                                            {verifyRules(user.rules, 'campanhas') && (
                                                <SidebarMenuButton
                                                    title={t('sidebar.tracking')}
                                                    slug="dashboard/marketing/rastreamento"
                                                    isSubItem
                                                    onClick={() => handleNavigate('/dashboard/marketing/rastreamento')}
                                                />
                                            )}
                                            {verifyRules(user.rules, 'campanhas') && (
                                                <SidebarMenuButton
                                                    title={t('sidebar.ctwa')}
                                                    slug="dashboard/marketing/ctwa"
                                                    isSubItem
                                                    tag={t('sidebar.newBadge')}
                                                    onClick={() => handleNavigate('/dashboard/marketing/ctwa')}
                                                />
                                            )}
                                            {hasFeatureAccess(cliente, user, FEATURE_KEYS.MARKETING_POSTS) && (
                                                <SidebarMenuButton
                                                    title={t('sidebar.posts')}
                                                    slug="dashboard/marketing/publicacoes"
                                                    isSubItem
                                                    onClick={() => handleNavigate('/dashboard/marketing/publicacoes')}
                                                />
                                            )}
                                            {podeVerAnunciosMeta && (
                                                <SidebarMenuButton
                                                    title="Anúncios Meta"
                                                    slug="dashboard/marketing/anuncios"
                                                    isSubItem
                                                    onClick={() => handleNavigate('/dashboard/marketing/anuncios')}
                                                />
                                            )}
                                        </SidebarExpandableMenu>
                                    )}

                                    {/* Admin */}
                                    {(isAdmin || verifyRules(user.rules, 'lider_setor')) && (
                                        <>
                                            <div className={ds.nav.divider} />
                                            <SidebarGroup label={t('sidebar.groups.admin')}>
                                                <SidebarMenuButton
                                                    title={t('sidebar.metrics')}
                                                    slug="dashboard/home"
                                                    icon="lucide:layout-dashboard"
                                                    onClick={() => handleNavigate('/dashboard/home')}
                                                />
                                                <SidebarMenuButton
                                                    title={t('sidebar.supervise')}
                                                    slug="dashboard/supervisao"
                                                    icon="lucide:eye"
                                                    onClick={() => handleNavigate('/dashboard/supervisao/lista')}
                                                />

                                                {(verifyRules(user.rules, 'relatorios') || verifyRules(user.rules, 'lider_setor')) && (
                                                    <SidebarExpandableMenu title={t('sidebar.reports')} icon="lucide:file-bar-chart">
                                                        <SidebarMenuButton
                                                            title={t('sidebar.reviews')}
                                                            slug="dashboard/relatorios/chats-avaliacoes"
                                                            isSubItem
                                                            onClick={() => handleNavigate('/dashboard/relatorios/chats-avaliacoes')}
                                                        />
                                                        <SidebarMenuButton
                                                            title={t('sidebar.tickets')}
                                                            slug="dashboard/relatorios/chamados"
                                                            isSubItem
                                                            onClick={() => handleNavigate('/dashboard/relatorios/chamados')}
                                                        />
                                                    </SidebarExpandableMenu>
                                                )}

                                                {user.level === 'root' && (
                                                    <SidebarExpandableMenu title={t('sidebar.ai')} icon="lucide:brain">
                                                        <SidebarMenuButton
                                                            title={t('sidebar.serviceAnalysis')}
                                                            slug="dashboard/ai/analises"
                                                            isSubItem
                                                            onClick={() => handleNavigate('/dashboard/ai/analises')}
                                                        />
                                                    </SidebarExpandableMenu>
                                                )}
                                            </SidebarGroup>
                                        </>
                                    )}
                                </nav>
                            </div>

                            {/* Footer com acoes */}
                            {footerExpanded}
                        </aside>
                    </SidebarContext.Provider>
                </Drawer>

                {/* Spacer para o header */}
                <div className="h-14 flex-shrink-0" />
            </SidebarContext.Provider>
        );
    }

    // Desktop: sidebar normal
    return (
        <SidebarContext.Provider value={{ isExpanded }}>
            {sidebarContent}
        </SidebarContext.Provider>
    );
}
